import {pushHookEvent} from "./push-hook-event.js"
// Sheet — bottom-sheet behaviour for <dialog class="sheet"> (see
// CoreComponents.sheet/1).
//
// Opening: dispatch `sheet:open` on the dialog (JS.dispatch("sheet:open",
// to: "#more-sheet")), or render it with data-open-on-mount. Closing: the X
// button, scrim tap, Escape, a downward swipe on the grab handle, the
// browser/hardware Back button (a history entry is pushed on open), or live
// navigation away from the page.
//
// LiveView note: `open` is browser-set state. app.js preserves it across
// patches (dom.onBeforeElUpdated) and the static shell sheets are rendered
// with phx-update="ignore", so a server render never closes a sheet by
// accident; a LiveView closes its own sheet by removing it (`:if`).

const SWIPE_CLOSE_PX = 80

// Pure: what to do with history when a sheet closes. We only pop the entry
// we pushed, and never when the close *came from* a pop.
export function historyActionOnClose(state, id, closedByPop) {
  if (closedByPop) return "none"
  return state && state.sheet === id ? "back" : "none"
}

// Pure: does a navigation event mean the sheet should close? Patches (a
// filter sheet updating the URL) keep it open; redirects and errors close it.
export function closesOnNavigation(kind) {
  return kind === "redirect" || kind === "error"
}

export default {
  mounted() {
    const d = this.el
    this.closedByPop = false
    this.pushed = false

    this.open = () => {
      if (d.open) return
      try {
        d.showModal()
      } catch (_e) {
        d.setAttribute("open", "")
      }
      history.pushState({...history.state, sheet: d.id}, "")
      this.pushed = true
      const title = d.querySelector(".sheet__title, [tabindex='-1']")
      if (title) title.focus({preventScroll: true})
    }

    this.close = () => {
      if (d.open) d.close()
    }

    this.onClose = () => {
      d.style.translate = ""
      d.style.transition = ""
      const action = historyActionOnClose(history.state, d.id, this.closedByPop)
      this.closedByPop = false
      this.pushed = false
      if (action === "back") history.back()
      const event = d.dataset.onClose
      if (event) pushHookEvent(this, event, {})
    }

    this.onPop = () => {
      if (d.open) {
        this.closedByPop = true
        d.close()
      }
    }

    // The dialog has no padding, so a click whose target is the dialog itself
    // landed on the backdrop. (`closedby="any"` does this natively where
    // supported.)
    this.onClick = (e) => {
      if (e.target === d) d.close()
    }

    this.onNav = (e) => {
      if (d.open && closesOnNavigation(e.detail && e.detail.kind)) d.close()
    }

    d.addEventListener("sheet:open", this.open)
    d.addEventListener("sheet:close", this.close)
    d.addEventListener("close", this.onClose)
    d.addEventListener("click", this.onClick)
    d.querySelectorAll("[data-sheet-close]").forEach((b) => b.addEventListener("click", this.close))
    window.addEventListener("popstate", this.onPop)
    window.addEventListener("phx:page-loading-start", this.onNav)
    this.bindSwipe(d.querySelector("[data-sheet-grab]"))

    if (d.hasAttribute("data-open-on-mount")) this.open()
  },

  destroyed() {
    window.removeEventListener("popstate", this.onPop)
    window.removeEventListener("phx:page-loading-start", this.onNav)
    // A LiveView removed an open sheet (`:if` flipped): drop our history entry
    // so Back doesn't land on a ghost state.
    if (this.pushed && history.state && history.state.sheet === this.el.id) history.back()
  },

  bindSwipe(handle) {
    if (!handle) return
    const d = this.el
    let startY = null

    handle.addEventListener("pointerdown", (e) => {
      startY = e.clientY
      handle.setPointerCapture(e.pointerId)
      d.style.transition = "none"
    })

    handle.addEventListener("pointermove", (e) => {
      if (startY == null) return
      const dy = Math.max(0, e.clientY - startY)
      d.style.translate = `0 ${dy}px`
    })

    const end = (e) => {
      if (startY == null) return
      const dy = e.clientY - startY
      startY = null
      d.style.transition = ""
      if (dy > SWIPE_CLOSE_PX) {
        d.close()
      } else {
        d.style.translate = ""
      }
    }

    handle.addEventListener("pointerup", end)
    handle.addEventListener("pointercancel", end)
  },
}
